import * as React from "react";
import { KeyValue, Query } from "../../../interfaces";
import SODStateList from "./SODStateList";
import { TableRow, TableCell, Button, Chip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { fetchData } from "../../../common/services/ApiClient";
import useOpenCloseAll from "../../../hooks/useOpenCloseAll";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";
import { ApiBaseUrl } from "../../../config";
import {
  arraySum,
  removeDuplicateArrayObj,
} from "../../../common/services/Utilities";
interface Props {
  appState: Query;
  openClose: string;
  setOpenClose: (openClose: string) => void;
  openCloseAllDisplay: KeyValue;
  setOpenCloseAllDisplay: (openCloseAll: KeyValue) => void;
}

const useStyles = makeStyles(theme => ({
  chip: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },
  tableRowStyles: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#acdfdeb8",
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body": {
      cursor: "pointer",
      filter: "brightness(.9)",
    },
  },
  footerRow: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      fontWeight: "bold",
    },
  },
  openCloseAll: {
    "minWidth": 80,
    "margin": 0,
    "color": theme.palette.primary.main,
    "fontWeight": 600,
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  rowIndent: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
  },
  stateButton: {
    margin: 0,
    fontWeight: "bold",
    color: theme.palette.primary.main,
    textAlign: "left",
  },
}));

export default function BHCList({
  appState,
  openClose,
  setOpenClose,
  openCloseAllDisplay,
  setOpenCloseAllDisplay,
}: Props) {
  const classes = useStyles();

  const [open, setOpen] = React.useState<string[]>([]);
  const [openCloseStates, setOpenCloseStates] = React.useState("");
  const [bhcData, setBhcData] = React.useState<KeyValue[]>([]);
  const [bhcAggResponse, setBhcAggResponse] = React.useState<KeyValue>({
    data: [],
    totals: {},
  });

  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const [openAllButton, setOpenAllButton] = React.useState<KeyValue>({});
  const [marketSelectSumCall, setMakeMarketSelectSumCall] = React.useState(
    false,
  );
  useOpenCloseAll({
    data: bhcData,
    key: "CERT",
    currentValues: open,
    openClose: openClose === "ALL",
    parent: "ALL",
    setValues: setOpen,
    setParent: setOpenClose,
    buttonDisplays: openCloseAllDisplay,
    setButtonDisplays: setOpenCloseAllDisplay,
  });

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      ui: appState.ui.marketSelection,
    };
    if (
      Object.keys(appState.ui.marketSelection[0]).length > 0 &&
      appState.filters.stateNames[0] !== "ALL" &&
      appState.filters.bhcRSSD &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setOpen([]);
      setPreviousSearch(filtersSearch);
      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          return fetchData(
            `${ApiBaseUrl()}/sod`,
            generateBranchOfficeDepositsApiUrl(
              appState,
              undefined,
              state,
              appState.ui.marketSelection[index],
              "CERT",
            ),
          );
        });

      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls)
          .then(response => {
            if (isMounted) {
              const data = response.map((res: any) => res.data);

              setBhcData(
                removeDuplicateArrayObj([].concat.apply([], data), "CERT"),
              );
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setMakeMarketSelectSumCall(true);
            isMounted = false;
          });
      })();
    }
  }, [appState, previousSearch]);

  React.useEffect(() => {
    if (marketSelectSumCall) {
      setOpen([]);
      setMakeMarketSelectSumCall(false);
      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          return fetchData(
            `${ApiBaseUrl()}/sod`,
            generateBranchOfficeDepositsApiUrl(
              appState,
              { by: "CERT", sum: ["DEPSUMBR"] },
              state,
              appState.ui.marketSelection[index],
            ),
          );
        });

      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls)
          .then(response => {
            if (isMounted) {
              const data = [].concat.apply(
                [],
                response.map((res: any) => res.data).filter(resData => resData),
              );

              const count = arraySum(
                data.map((v: any) => {
                  return { [v.CERT]: v.count };
                }),
              );

              const depsum = arraySum(
                data.map((v: any) => {
                  return { [v.CERT]: v.sum_DEPSUMBR };
                }),
              );

              const newData = Object.keys(count).map((val: any) => {
                return {
                  CERT: val,
                  count: count[val],
                  sum_DEPSUMBR: depsum[val],
                };
              });
              const totals = response.map((res: any) => res.totals);
              setBhcAggResponse({
                data: newData,
                totals: arraySum(totals),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, marketSelectSumCall]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
    };

    if (
      appState.filters.bhcRSSD &&
      (Object.keys(appState.ui.marketSelection[0]).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setOpen([]);
      setPreviousSearch(filtersSearch);
      const fetchUrls = [
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            appState,
            undefined,
            undefined,
            undefined,
            "CERT",
          ),
        ),
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(appState, {
            by: "CERT",
            terms: [""],
            sum: ["DEPSUMBR"],
          }),
        ),
      ];
      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls)
          .then(response => {
            if (isMounted) {
              setBhcData(response[0].data);
              setBhcAggResponse({
                data: response[1].data,
                totals: response[1].totals,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [appState, previousSearch]);

  const bhcRows = bhcData.map((data, int) => {
    const filteredAggData = bhcAggResponse.data.filter(
      (aggData: KeyValue) => Number(aggData.CERT) === Number(data.CERT),
    )[0];
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        setOpenCloseAllDisplay({
          ...openCloseAllDisplay,
          ALL: {
            ...openCloseAllDisplay.ALL,
            [data.CERT]: !open.includes(data.CERT) ? true : false,
          },
        });
        open.includes(data.CERT) && setOpenAllButton({});
        !open.includes(data.CERT)
          ? setOpen([...open, data.CERT])
          : setOpen(open.filter(institution => institution !== data.CERT));
      }
    };
    return (
      <React.Fragment key={`${data.CERT}_${int}`}>
        <TableRow
          className={classes.tableRowStyles}
          tabIndex={0}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              expandCollapse(e);
            }
          }}
          onClick={(e: any) => expandCollapse(e)}
        >
          <TableCell className={classes.rowIndent}>
            {open.includes(data.CERT) ? (
              <Chip
                className={classes.chip}
                icon={<ExpandMoreIcon />}
                label={data.NAMEFULL}
              />
            ) : (
              <Chip
                className={classes.chip}
                icon={<ChevronRightIcon />}
                label={data.NAMEFULL}
              />
            )}
          </TableCell>
          <TableCell>
            {openAllButton[data.CERT] &&
              Object.keys(openAllButton[data.CERT]).length > 1 &&
              open.includes(data.CERT) && (
                <Button
                  id={
                    Object.values(openAllButton[data.CERT]).every(val => val)
                      ? `closeAllStates_${data.CERT}`
                      : `openAllStates_${data.CERT}`
                  }
                  className={classes.openCloseAll}
                  onClick={() => {
                    setOpenCloseStates(data.CERT);
                  }}
                >
                  {Object.values(openAllButton[data.CERT]).every(val => val)
                    ? "Close All"
                    : "Open All"}
                </Button>
              )}
          </TableCell>
          <TableCell>{data.ADDRESS}</TableCell>
          <TableCell>{data.CITY} </TableCell>
          <TableCell>{data.ZIP}</TableCell>
          <TableCell>{filteredAggData?.count?.toLocaleString()}</TableCell>
          <TableCell>{data.CERT}</TableCell>
          <TableCell>{data.BKCLASS}</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell style={{ textAlign: "right" }}>
            {filteredAggData?.sum_DEPSUMBR?.toLocaleString()}
          </TableCell>
        </TableRow>
        {open.includes(data.CERT) && (
          <SODStateList
            appState={appState}
            cert={data.CERT}
            fromBHC
            parentName={data.CERT}
            openClose={openCloseStates}
            setOpenClose={setOpenCloseStates}
            openCloseAllDisplay={openAllButton}
            setOpenCloseAllDisplay={setOpenAllButton}
          />
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {bhcRows}
      <TableRow className={classes.footerRow}>
        <TableCell style={{ textAlign: "left", paddingLeft: "1rem" }}>
          Totals
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell>{bhcAggResponse.totals?.count?.toLocaleString()}</TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell style={{ textAlign: "right" }}>
          {bhcAggResponse.totals?.sum_DEPSUMBR?.toLocaleString()}
        </TableCell>
      </TableRow>
    </>
  );
}
